import React, { useState, useEffect } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Space, Button, Input, Form, Modal, Tooltip, AutoComplete } from 'antd';
import { NotificationInstance } from 'antd/es/notification/interface';

import { PlusOutlined } from '@ant-design/icons';

import IBaseComponentProps from '@components/base-component-props';

import { exception } from '@extensions/notification';
import { IOption } from '@extensions/option';

import { useAppDispatch } from '@store/hooks';
import { userLoaded } from '@store/actions';

import { ICounterparty } from '@entities/counterparty';

dayjs.extend(utc);

interface ICounterpartyParams extends IBaseComponentProps {
    userId: string | undefined;
    legalName: string | undefined;
    baseCounterparties: Array<ICounterparty> | undefined;
    disabled?: boolean;
    hideAdd?: boolean;
    api: NotificationInstance;
    onSelect: (value: ICounterparty | undefined) => void;
    loadCounterparties: () => void;
}

const Counterparty = (props: ICounterpartyParams) => {
    const { userId, baseCounterparties, legalName, api, disabled, hideAdd, loadCounterparties, onSelect, serverFetch } = props;

    const { TextArea } = Input;

    const [form] = Form.useForm();

    const d = useAppDispatch();

    const [counterparties, setCounterparties] = useState<Array<ICounterparty>>([]);
    const [options, setOptions] = useState<Array<IOption>>([]);
    const [counterpartyOpen, setOpenCounterparty] = useState<boolean>(false);

    useEffect(() => {
        if (!baseCounterparties) return;

        setCounterparties(baseCounterparties);
    }, [baseCounterparties]);

    useEffect(() => {
        const items: Array<IOption> = [];

        counterparties.map((c: ICounterparty) => {
            const newItem: IOption = {
                id: c.id,
                value: `${c.legalName}`,
                label: `${c.legalName} / ${c.inn}`,
            };

            items.push(newItem);
        });

        setOptions(items);
    }, [counterparties]);

    const onSaveCunterparty = (entity: ICounterparty) => {
        entity.userId = userId;

        serverFetch(`counterparties`, { method: 'POST', bodyData: entity })
            .then(() => {
                form.resetFields();
                setOpenCounterparty(false);
                loadCounterparties();
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка сохранения контрагента', ex, () => d(userLoaded(undefined)));
            });
    };

    return (
        <>
            <Space.Compact style={{ width: '100%' }}>
                <AutoComplete
                    allowClear
                    placeholder='Контрагент'
                    value={legalName}
                    disabled={disabled}
                    popupMatchSelectWidth={false}
                    options={options}
                    onClear={() => {
                        onSelect(undefined);
                    }}
                    onSelect={(data, option: IOption) => {
                        const counterparty = counterparties.find((c) => c.id == option.id);
                        onSelect(counterparty);
                    }}
                    showSearch={{
                        onSearch: async (value: string) => {
                            const result = baseCounterparties?.filter((p) => p.legalName?.toUpperCase()?.startsWith(value.toUpperCase()));
                            setCounterparties(result || []);
                        },
                    }}
                />
                {!hideAdd && (
                    <Tooltip title='Добавить торговую площадку'>
                        <Button icon={<PlusOutlined />} onClick={() => setOpenCounterparty(true)} />
                    </Tooltip>
                )}
            </Space.Compact>

            <Modal
                title='Торговая площадка'
                open={counterpartyOpen}
                okText='ОК'
                onOk={() => form.submit()}
                onCancel={() => {
                    form.resetFields();

                    setOpenCounterparty(false);
                }}
            >
                <Form
                    preserve={false}
                    colon={false}
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 18 }}
                    style={{ marginTop: 20 }}
                    onFinish={onSaveCunterparty}
                    form={form}
                >
                    <Form.Item
                        label='Название'
                        name='legalName'
                        required={true}
                        rules={[{ required: true, message: 'Укажите контрагента' }]}
                    >
                        <Input autoFocus />
                    </Form.Item>
                    <Form.Item label='ИНН' name='inn' required={true} rules={[{ required: true, message: 'Укажите ИНН' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label='Юр. адрес'
                        name='legalAddress'
                        required={true}
                        rules={[{ required: true, message: 'Укажите юридический адрес' }]}
                    >
                        <TextArea />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default Counterparty;
